import { useCallback, useEffect, useRef, useState } from "react";
import {
  acknowledgeTelecallingNotification,
  fetchTelecallingNotificationSnapshot,
} from "../services/telecallingRecordsApi";

const POLL_INTERVAL_MS = 120 * 1000;
const INSERT_RECHECK_DELAY_MS = 1000;

function authenticatedUsername(authenticatedUser) {
  if (typeof authenticatedUser === "string") {
    return authenticatedUser;
  }

  return authenticatedUser?.username || "";
}

function useTelecallingNotification(authenticatedUser) {
  const username = authenticatedUsername(authenticatedUser);
  const [notification, setNotification] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [acknowledgeError, setAcknowledgeError] = useState("");
  const notificationRef = useRef(null);
  const isMountedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const isAcknowledgingRef = useRef(false);
  const queuedFetchRef = useRef(false);
  const retryTimerRef = useRef(null);
  const fetchGenerationRef = useRef(0);
  const fetchSnapshotRef = useRef(null);

  const updateNotification = useCallback((nextNotification) => {
    notificationRef.current = nextNotification;
    setNotification(nextNotification);
  }, []);

  const fetchSnapshot = useCallback(
    async ({ allowInsertRecheck = true } = {}) => {
      if (!isMountedRef.current) {
        return;
      }

      if (isFetchingRef.current) {
        queuedFetchRef.current = true;
        return;
      }

      isFetchingRef.current = true;
      const requestGeneration = fetchGenerationRef.current;

      try {
        const snapshot = await fetchTelecallingNotificationSnapshot();

        if (
          !isMountedRef.current ||
          requestGeneration !== fetchGenerationRef.current
        ) {
          return;
        }

        if (!notificationRef.current && snapshot.notification) {
          updateNotification(snapshot.notification);
        }

        if (
          allowInsertRecheck &&
          snapshot.insertedCount > 0 &&
          !snapshot.notification &&
          !retryTimerRef.current
        ) {
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            fetchSnapshotRef.current?.({ allowInsertRecheck: false });
          }, INSERT_RECHECK_DELAY_MS);
        }
      } catch {
        // Polling errors are transient. Keep any alert visible and retry later.
      } finally {
        isFetchingRef.current = false;

        if (queuedFetchRef.current && isMountedRef.current) {
          queuedFetchRef.current = false;
          fetchSnapshotRef.current?.();
        }
      }
    },
    [updateNotification],
  );

  fetchSnapshotRef.current = fetchSnapshot;

  useEffect(() => {
    isMountedRef.current = true;

    const pollWhenVisible = () => {
      if (document.visibilityState === "visible") {
        fetchSnapshotRef.current?.();
      }
    };
    const intervalId = window.setInterval(pollWhenVisible, POLL_INTERVAL_MS);

    pollWhenVisible();
    document.addEventListener("visibilitychange", pollWhenVisible);

    return () => {
      isMountedRef.current = false;
      queuedFetchRef.current = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", pollWhenVisible);

      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [username]);

  const acknowledge = useCallback(async () => {
    const currentNotification = notificationRef.current;
    const phone =
      currentNotification?.notif_phone || currentNotification?.phone || "";

    if (!phone || !username || isAcknowledgingRef.current) {
      return;
    }

    isAcknowledgingRef.current = true;
    setIsAcknowledging(true);
    setAcknowledgeError("");

    try {
      const result = await acknowledgeTelecallingNotification({
        phone,
        agent: username,
      });
      const wasAcknowledged = result.acknowledged === true;
      const wasStale =
        result.success === true &&
        (result.acknowledged === false || Number(result.affected_rows) === 0);

      if (wasAcknowledged || wasStale) {
        fetchGenerationRef.current += 1;
        updateNotification(null);
        fetchSnapshotRef.current?.();
      }
    } catch (error) {
      if (isMountedRef.current) {
        setAcknowledgeError(error.message);
      }
    } finally {
      isAcknowledgingRef.current = false;

      if (isMountedRef.current) {
        setIsAcknowledging(false);
      }
    }
  }, [updateNotification, username]);

  return {
    notification,
    isAcknowledging,
    acknowledgeError,
    acknowledge,
  };
}

export default useTelecallingNotification;
